import _ from 'lodash';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import type { Store } from 'redux';

import { showErrorAction, showMessageAction } from 'containers/Notification/actions';
import AutoSourcingRulesApi from 'entities/autoSourcingRule/api';
import { selectAutoSourcingRules } from 'entities/autoSourcingRule/selectors';
import { selectOrganization, selectUserLocations } from 'entities/user/selectors';
import type { State } from 'reducers';
import LocationsApi from 'entities/location/api';

import {
  INIT,
  InitAction,
  initSuccessAction,
  initFailedAction,
  SUBMIT,
  SubmitAction,
  submitSuccessAction,
  submitFailedAction,
} from './types';

const init = (action$: ActionsObservable<InitAction>, store: Store<State>) =>
  action$.ofType(INIT).mergeMap(() => {
    const state = store.getState();

    const actions = [];

    const autoSourcingRules = selectAutoSourcingRules(state);

    if (autoSourcingRules.size === 0) {
      actions.push(AutoSourcingRulesApi.fetchAll());
    }

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationsApi.fetchAll());
    }

    if (actions.length === 0) {
      return Observable.of(initSuccessAction());
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch(() =>
        Observable.of(
          initFailedAction(),
          showErrorAction('An error occurred during loading setting.')
        )
      );
  });

const submit = (action$: ActionsObservable<SubmitAction>, store: Store<State>) =>
  action$.ofType(SUBMIT).mergeMap(({ data }) => {
    if ('id' in data && data?.id) {
      return AutoSourcingRulesApi.update(data.id, data)
        .mergeMap((resultingAction) => [
          resultingAction,
          showMessageAction('Setting successfully saved!'),
          submitSuccessAction(),
        ])
        .catch(() => [showErrorAction('Saving failed'), submitFailedAction()]);
    } else {
      const state = store.getState();
      const organization = selectOrganization(state);

      const extraData = {
        organization: organization?.id,
        title: 'AutoSourcingRule',
      };

      return AutoSourcingRulesApi.create({ ...extraData, ...data })
        .mergeMap((resultingAction) => [
          resultingAction,
          showMessageAction('Setting successfully saved!'),
          submitSuccessAction(),
        ])
        .catch(() => [showErrorAction('Saving failed'), submitFailedAction()]);
    }
  });

export default [init, submit];
